// ./src/components/features/Connectors/ConnectorTypeSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import {
  CONNECTOR_TYPES,
  CONNECTOR_TYPE_LABELS,
  CONNECTOR_CATEGORY_TO_TYPES,
} from "@/config/connectorCategories";

export interface ConnectorTypeSelectorProps {
  /** Selected type value */
  value: string | null | undefined;

  /** Callback when selection changes */
  onChange?: (type: string | null) => void;

  /** Selected category to filter types */
  category?: string | null;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Field label */
  label?: string;

  /** Field description */
  description?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show all types regardless of category */
  showAllTypes?: boolean;
}

export const ConnectorTypeSelector: React.FC<ConnectorTypeSelectorProps> = ({
  value,
  onChange,
  category,
  placeholder = "Select a type",
  required = false,
  label,
  description,
  error,
  disabled,
  size = "md",
  showAllTypes = false,
}) => {
  // Build options based on category filter
  const typeOptions = useMemo(() => {
    let availableTypes: string[];

    if (showAllTypes || !category) {
      // Show all types if no category selected or showAllTypes is true
      availableTypes = Object.values(CONNECTOR_TYPES);
    } else {
      // Filter types by category
      availableTypes = CONNECTOR_CATEGORY_TO_TYPES[category] || [];
    }

    return availableTypes.map((type) => ({
      value: type,
      label: CONNECTOR_TYPE_LABELS[type] || type,
    }));
  }, [category, showAllTypes]);

  // Clear selection if current value is not in available options
  useEffect(() => {
    if (value && !showAllTypes && category) {
      const availableTypes = CONNECTOR_CATEGORY_TO_TYPES[category] || [];
      if (!availableTypes.includes(value)) {
        onChange?.(null);
      }
    }
  }, [category, value, onChange, showAllTypes]);

  // Auto-select first option if required, category is selected, and no value
  useEffect(() => {
    if (
      required &&
      !value &&
      typeOptions.length > 0 &&
      (showAllTypes || category)
    ) {
      onChange?.(typeOptions[0].value);
    }
  }, [required, value, typeOptions, showAllTypes, category, onChange]);

  // Handle change
  const handleChange = (selectedValue: string | null) => {
    onChange?.(selectedValue);
  };

  // Determine if selector should be disabled
  const isDisabled = disabled || (!showAllTypes && !category);
  const effectivePlaceholder =
    !showAllTypes && !category ? "Select a category first" : placeholder;

  return (
    <Select
      label={label}
      description={description}
      placeholder={effectivePlaceholder}
      data={typeOptions}
      value={value ?? null}
      onChange={handleChange}
      required={required}
      withAsterisk={required}
      clearable={!required}
      disabled={isDisabled}
      error={error}
      size={size}
    />
  );
};
